import { z } from 'zod';

/** `remote`: mic permission requested after a user gesture. `in_person`: mics start off to avoid feedback. `off`: no voice. */
export const VoiceMode = z.enum(['remote', 'in_person', 'off']);
export type VoiceMode = z.infer<typeof VoiceMode>;

/**
 * Module-declared voice scope. `shared`: one session room for everyone (default).
 * `teams`: server-authorised team channels for the module's team assignments while it is active;
 * the shared room stays reserved and is rejoined when the module completes.
 */
export const VoiceScope = z.enum(['shared', 'teams']);
export type VoiceScope = z.infer<typeof VoiceScope>;

export const VoiceRoomAssignment = z.object({
  roomName: z.string(),
  scope: z.enum(['shared', 'team']),
  teamId: z.string().nullable(),
  /** Server-authorised membership. Credentials are only issued to members. */
  participantIds: z.array(z.string()),
});
export type VoiceRoomAssignment = z.infer<typeof VoiceRoomAssignment>;

export const VoiceProvider = z.enum(['mock', 'livekit']);
export type VoiceProvider = z.infer<typeof VoiceProvider>;

/** Public voice information included in every session snapshot. Never includes provider secrets. */
export const VoicePublic = z.object({
  provider: VoiceProvider,
  /** True when no live provider is configured or the session runs simulated participants. UI must label it. */
  simulated: z.boolean(),
  mode: VoiceMode,
  scope: VoiceScope,
  rooms: z.array(VoiceRoomAssignment),
});
export type VoicePublic = z.infer<typeof VoicePublic>;

/** Scoped credentials issued per participant per room by the server (HTTP, authenticated with the participant token). */
export const VoiceCredentials = z.discriminatedUnion('provider', [
  z.object({
    provider: z.literal('mock'),
    simulated: z.literal(true),
    roomName: z.string(),
    identity: z.string(),
  }),
  z.object({
    provider: z.literal('livekit'),
    simulated: z.literal(false),
    url: z.string(),
    token: z.string(),
    roomName: z.string(),
    identity: z.string(),
    expiresAt: z.number(),
  }),
]);
export type VoiceCredentials = z.infer<typeof VoiceCredentials>;

export const VoiceConnectionStatus = z.enum([
  'idle',
  'connecting',
  'connected',
  'reconnecting',
  'permission_denied',
  'device_error',
  'failed',
  'disconnected',
]);
export type VoiceConnectionStatus = z.infer<typeof VoiceConnectionStatus>;

/** Client-side adapter contract. Implementations: mock (labelled simulated) and LiveKit. Gameplay never depends on it. */
export interface VoiceClient {
  readonly provider: VoiceProvider;
  connect(credentials: VoiceCredentials): Promise<void>;
  disconnect(): Promise<void>;
  setMuted(muted: boolean): Promise<void>;
  /** Optional browser audio unlock, called directly from a user gesture when autoplay is blocked. */
  startAudio?(): Promise<boolean>;
  isAudioPlaybackBlocked?(): boolean;
  isMuted(): boolean;
  status(): VoiceConnectionStatus;
  /** Identities currently speaking (provider-driven or simulated). */
  onSpeaking(listener: (identities: string[]) => void): () => void;
  onStatus(listener: (status: VoiceConnectionStatus, detail?: string) => void): () => void;
}
